import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Button, ButtonVariant } from '@sas/nova-commons/components/Button';
import { GeoUILocationPin, GeoUIYourLocation, LinkBroken, AddRoute, GeoUIAreaAddress, Location, } from '@sas/react-svg';
import { Divider } from '@sas/nova-commons/components/Divider';
import { Label } from '@sas/nova-commons/components/Label';
import { Icon } from '@sas/nova-commons/components/Icon';
import { Flow } from '@sas/nova-commons/components/Layouts/Flow';
import { Input, useInputValidationAdapter, validateTextInput, } from '@sas/nova-commons/components/Input';
import { TextArea } from '@sas/nova-commons/components/TextArea';
import { ObjectSearchState, isValidLocation, } from '../../../../store/objectSearch/ObjectSearchState';
import { PanelHeader } from '../components/PanelHeader';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Item, List } from '@sas/nova-commons/components/List';
import { Heading } from '@sas/nova-commons/components/Heading';
import { useI18n } from '../../../../locale/I18nContext';
import { PanelListItem, panelItemHeight } from '../components/PanelListItem';
import { renderInlineCssVars, useDesignTokens, useGraphTokens, } from '@sas/nova-commons/theme';
import { selectObjectSearchState } from '../../../../store/selectors';
import { Popover } from '@sas/nova-commons/components/Popover';
import { IntervalDialog } from './Dialog';
import { PanelSearchField } from '../components/PanelSearchField';
import { useLatestRef, useOnUnmount } from '../../../hooks';
import { useMinPanelHeight } from '../../hooks/useMinPanelHeight';
import { createUseHtmlId } from '@sas/nova-commons/hooks/useHtmlId';
import { withLabels } from '@sas/nova-commons/components/withLabels';
import { getFeatureTokens } from '../../utils/tokens';
import { FeatureColorPicker } from '../components/FeatureColorPicker';
import './PinPanel.css';
import cn from './PinPanel.css.json';
const TextAreaWithLabels = withLabels(TextArea);
const usePinPanelId = createUseHtmlId('PinPanel');
/**
 * Primary-based text color, used for dialog headers
 */
function getColorTextPrimary(dt) {
    switch (dt.themeVariant) {
        case 'dark':
            return dt.reportTokens.c.p2;
        case 'high-contrast':
            return dt.reportTokens.c.p3;
        default:
            return dt.reportTokens.c.p;
    }
}
export const PinPanelBase = (props) => {
    var _a, _b, _c, _d;
    const localize = useI18n();
    const dt = useDesignTokens();
    const gt = useGraphTokens();
    const tokens = renderInlineCssVars({
        'spacing-s': dt.tokens['space-base-s'],
        'spacing-m': dt.tokens['space-base-m'],
        bg: dt.reportTokens.backgrounds.panes.bg,
        'inline-s': dt.tokens['space-base-s'],
    }, 'pin');
    const [selectedLinkedItems, setSelectedLinkedItems] = useState(() => new Set());
    const inputRef = useRef(null);
    const [addGeoAreaPopoverOpen, setAddGeoAreaPopoverOpen] = useState(false);
    const addGeoAreaButtonRef = useRef(null);
    const [intervalDialogIsOpen, setIntervalDialogIsOpen] = useState(false);
    const [pinDescription, setPinDescription] = useState();
    const [headingId, labelId] = usePinPanelId(2);
    const validLocation = isValidLocation(props.pin.latLon);
    const headerRef = useRef(null);
    const footerRef = useRef(null);
    useMinPanelHeight(headerRef, footerRef, (minPanelHeight) => {
        props.actions.update(props.id, { minPanelHeight });
    });
    useOnUnmount(() => {
        // Disable pin mode on unmount
        props.actions.update(props.id, {
            pinModeEnabled: false,
            locationCandidate: undefined,
            searchHints: [],
        });
    });
    // returns {true} if the given {GeographicFeature} is linked to featureIndex
    const linkedToFeature = (item) => {
        var _a;
        return ((item.type === 'route' &&
            ((_a = item.routeLocations) === null || _a === void 0 ? void 0 : _a.filter((routeLocationOrLinkedIndex) => typeof routeLocationOrLinkedIndex === 'number').includes(props.featureIndex))) ||
            (item.type === 'area' && item.linkedPinIndex === props.featureIndex));
    };
    const linkedItems = (_b = (_a = props.features) === null || _a === void 0 ? void 0 : _a.filter(linkedToFeature).map((item) => {
        return {
            item,
            // TODO: add onVisibilityButtonPress, onNavigationButtonPress
        };
    })) !== null && _b !== void 0 ? _b : [];
    const unlinkItems = () => {
        var _a;
        const indices = [];
        if (!props.features) {
            return;
        }
        if (selectedLinkedItems.size > 0) {
            let linkedItemsIndex = 0;
            for (const [index, feature] of props.features.entries()) {
                if (linkedToFeature(feature)) {
                    if (selectedLinkedItems.has(linkedItemsIndex.toString())) {
                        indices.push({ index, type: feature.type });
                    }
                    linkedItemsIndex++;
                }
            }
            setSelectedLinkedItems(new Set());
        }
        else {
            for (const [index, feature] of (_a = props.features) === null || _a === void 0 ? void 0 : _a.entries()) {
                if (linkedToFeature(feature)) {
                    indices.push({ index, type: feature.type });
                }
            }
        }
        return props.actions.updateItemStates(props.id, {
            indices,
            property: 'linkedPinIndex',
            value: undefined,
        });
    };
    const nameInputValidator = useInputValidationAdapter({
        isRequired: true,
        minLength: 1,
        initialValue: props.pin.name,
    });
    {
        const refHandleChange = useLatestRef(nameInputValidator.handleChange);
        useEffect(() => {
            refHandleChange.current({ value: props.pin.name });
        }, [refHandleChange, props.pin.name]);
    }
    let menuSections = [
        {
            items: [
                {
                    text: localize({
                        key: props.pin.description !== undefined
                            ? 'PinPanel.deleteDescription.txt'
                            : 'PinPanel.addDescription.txt',
                    }),
                },
                {
                    text: localize({ key: 'PinPanel.newPin.txt' }),
                    disabled: !validLocation,
                },
                {
                    text: localize({
                        key: props.pin.type === 'pin'
                            ? 'PinPanel.deletePin.txt'
                            : 'PinPanel.deleteMyLocationPin.txt',
                    }),
                },
            ],
        },
    ];
    if (props.linkingEnabled) {
        menuSections = menuSections.concat([
            {
                items: [
                    {
                        text: localize({
                            key: selectedLinkedItems.size > 0
                                ? 'PinPanel.unlinkItems.txt'
                                : 'PinPanel.unlinkAllItems.txt',
                        }),
                        disabled: linkedItems.length === 0,
                    },
                ],
            },
        ]);
    }
    if (props.pin.type === 'myLocationPin') {
        menuSections = menuSections.concat([
            {
                items: [
                    {
                        text: localize({
                            key: props.pin.autoUpdateEnabled === true
                                ? 'PinPanel.disableAutoUpdate.txt'
                                : 'PinPanel.enableAutoUpdate.txt',
                        }),
                    },
                    {
                        text: localize({ key: 'PinPanel.editAutoRefreshInterval.txt' }),
                    },
                ],
            },
        ]);
    }
    useEffect(() => {
        setPinDescription(props.pin.description);
    }, [props.pin.description]);
    return (_jsxs("section", Object.assign({ className: cn.container, style: tokens, tabIndex: -1, "aria-labelledby": headingId }, { children: [_jsx(PanelHeader, { ref: headerRef, id: props.id, headingId: headingId, type: props.pin.type, text: localize({
                    key: props.pin.type === 'pin' ? 'PinPanel.pinTitle.txt' : 'PinPanel.myLocationTitle.txt',
                }), menuSections: menuSections, onMenuItemSelected: (location) => {
                    const sectionIndex = location[0][0];
                    const itemIndex = location[0][1];
                    if (sectionIndex === 0) {
                        switch (itemIndex) {
                            case 0:
                                if (props.pin.description !== undefined) {
                                    // Delete pin description
                                    props.actions.notifyUpdatePin(props.id, {
                                        index: props.featureIndex,
                                        update: {
                                            description: undefined,
                                        },
                                    });
                                }
                                else {
                                    // Add pin description
                                    props.actions.notifyUpdatePin(props.id, {
                                        index: props.featureIndex,
                                        update: {
                                            description: '',
                                        },
                                    });
                                }
                                break;
                            case 1:
                                // New pin at this location
                                props.actions.notifyNewPin(props.id, {
                                    sourceFeatureIndex: props.featureIndex,
                                    location: props.pin.location,
                                    latLon: props.pin.latLon,
                                });
                                break;
                            case 2:
                                // Delete pin
                                props.actions.notifyDeletePins(props.id, [props.featureIndex]);
                                break;
                            default:
                                break;
                        }
                    }
                    else if (sectionIndex === 1) {
                        switch (itemIndex) {
                            case 0:
                                if (props.linkingEnabled) {
                                    // Unlink all items
                                    unlinkItems();
                                }
                                else {
                                    // Enable My Location auto-update
                                    props.actions.notifyUpdatePin(props.id, {
                                        index: props.featureIndex,
                                        update: {
                                            autoUpdateEnabled: !props.pin.autoUpdateEnabled,
                                        },
                                    });
                                }
                                break;
                            case 1:
                                // Edit My Location auto-refresh interval...
                                setIntervalDialogIsOpen(true);
                                break;
                            default:
                                break;
                        }
                    }
                    else if (sectionIndex === 2) {
                        switch (itemIndex) {
                            case 0:
                                // Enable My Location auto-update
                                props.actions.notifyUpdatePin(props.id, {
                                    index: props.featureIndex,
                                    update: {
                                        autoUpdateEnabled: !props.pin.autoUpdateEnabled,
                                    },
                                });
                                break;
                            case 1:
                                // Edit My Location auto-refresh interval...
                                setIntervalDialogIsOpen(true);
                                break;
                            default:
                                break;
                        }
                    }
                } }), _jsxs("div", Object.assign({ className: cn.contentContainer }, { children: [_jsx(IntervalDialog, { isOpen: intervalDialogIsOpen, onDismiss: () => setIntervalDialogIsOpen(false), onPress: (interval) => {
                            props.actions.notifyUpdatePin(props.id, {
                                index: props.featureIndex,
                                update: {
                                    autoRefreshInterval: interval,
                                },
                            });
                            setIntervalDialogIsOpen(false);
                        }, interval: props.pin.autoRefreshInterval }), _jsxs("div", Object.assign({ className: cn.formScrollContainer }, { children: [_jsxs(Flow, Object.assign({ className: cn.formContent, orientation: "vertical", spacing: "s" }, { children: [props.pin.type === 'pin' && (_jsx(Label, Object.assign({ id: labelId, wrapText: false }, { children: localize({ key: 'PinPanel.label.txt' }) }))), _jsxs(Flow, Object.assign({ spacing: 's', orientation: "horizontal", className: cn.label }, { children: [_jsx(Icon, { className: cn.pinIcon, icon: props.pin.type === 'pin' ? GeoUILocationPin : GeoUIYourLocation, tokens: () => getFeatureTokens(dt, gt, props.pin), ariaHidden: true, tooltip: "" }), _jsx(Input, { ariaLabelledBy: labelId, className: cn.input, onChange: nameInputValidator.handleChange, value: nameInputValidator.value, onBlur: nameInputValidator.handleBlur, onCommit: (event) => {
                                                    nameInputValidator.handleCommit();
                                                    const result = validateTextInput({
                                                        value: event.value,
                                                        isRequired: true,
                                                        minLength: 1,
                                                    });
                                                    if (!result.isError) {
                                                        props.actions.notifyUpdatePin(props.id, {
                                                            index: props.featureIndex,
                                                            update: { name: event.value },
                                                        });
                                                    }
                                                }, message: (_c = nameInputValidator.errors) === null || _c === void 0 ? void 0 : _c.message, status: (_d = nameInputValidator.errors) === null || _d === void 0 ? void 0 : _d.status, onCancel: nameInputValidator.handleCancel }), _jsx(FeatureColorPicker, { id: props.id, feature: props.pin, featureIndex: props.featureIndex, onColorChange: (color) => {
                                                    props.actions.notifyUpdatePin(props.id, {
                                                        index: props.featureIndex,
                                                        update: { color },
                                                    });
                                                } })] })), props.pin.type === 'pin' ? (_jsx(PanelSearchField, { id: props.id, label: localize({ key: 'PinPanel.location.txt' }), labelHelp: {
                                            library: 'vaeua-gui-eua',
                                            contentKey: 'react.va.geo.search.pinLocation',
                                        }, feature: props.pin, ref: inputRef, hintIconOverride: Location, locationCandidate: props.locationCandidate, locationPopoverTitle: localize({ key: 'LocationPopover.pin.title.txt' }), onInvalidateLocation: () => props.actions.notifyUpdatePin(props.id, {
                                            index: props.featureIndex,
                                            update: { latLon: undefined, location: undefined },
                                        }), onUpdateHints: ({ searchTerm }) => {
                                            props.actions.notifyUpdateHints(props.id, {
                                                featureIndex: props.featureIndex,
                                                searchTerm,
                                            });
                                        }, onApplyHint: ({ hint, searchTerm }) => {
                                            props.actions.notifyApplyHint(props.id, {
                                                featureIndex: props.featureIndex,
                                                hint,
                                                searchTerm,
                                            });
                                        }, onUpdatePinMode: (enabled) => {
                                            props.actions.setPinModeEnabled(props.id, enabled);
                                        }, onConfirm: () => {
                                            var _a, _b;
                                            props.actions.update(props.id, {
                                                pinModeEnabled: false,
                                                locationCandidate: undefined,
                                            });
                                            props.actions.notifyUpdatePin(props.id, {
                                                index: props.featureIndex,
                                                update: Object.assign({ latLon: props.locationCandidate.latLon, location: (_a = props.locationCandidate.location) !== null && _a !== void 0 ? _a : undefined }, (((_b = props.locationCandidate) === null || _b === void 0 ? void 0 : _b.country) && {
                                                    country: { id3: props.locationCandidate.country },
                                                })),
                                            });
                                        }, onCancel: () => {
                                            props.actions.update(props.id, {
                                                pinModeEnabled: false,
                                                locationCandidate: undefined,
                                            });
                                        }, onOutsideClick: () => {
                                            props.actions.update(props.id, {
                                                locationCandidate: undefined,
                                            });
                                        } })) : (_jsx(Label, Object.assign({ style: renderInlineCssVars({ fg: dt.reportTokens.text.basic.fg }, 'myLocation'), className: cn.locationLabel, wrapText: false }, { children: props.pin.location }))), props.pin.description !== undefined && (_jsx(TextAreaWithLabels, { fieldWidth: "100%", wrapLabel: false, label: localize({ key: 'PinPanel.description.txt' }), value: pinDescription || '', onChange: ({ value }) => setPinDescription(value), onCommit: (event) => {
                                            props.actions.notifyUpdatePin(props.id, {
                                                index: props.featureIndex,
                                                update: {
                                                    description: event.value,
                                                },
                                            });
                                        }, rows: 3, className: cn.input }))] })), !!linkedItems.length && props.linkingEnabled && (_jsxs(Flow, Object.assign({ orientation: "vertical", spacing: "s" }, { children: [_jsxs(Flow, Object.assign({ orientation: "horizontal", className: cn.linkedListHeading, spacing: "s" }, { children: [_jsx(Heading, Object.assign({ className: cn.headingText, level: 5 }, { children: localize({ key: 'PinPanel.linked.txt' }) })), _jsx(Icon, { icon: LinkBroken, disabled: selectedLinkedItems.size === 0, onClick: unlinkItems })] })), _jsx(Divider, { variant: "standard" }), _jsx("div", Object.assign({ className: cn.linkedList }, { children: _jsx(List, { selectionMode: "desktop", rowCount: linkedItems.length, expandToFillWidth: true, itemHeight: () => 'double', height: panelItemHeight * linkedItems.length, renderItem: ({ index }) => {
                                                const item = linkedItems[index];
                                                return _jsx(PanelListItem, Object.assign({}, item));
                                            }, onSelect: (rows) => setSelectedLinkedItems(rows !== null && rows !== void 0 ? rows : new Set()) }) }))] })))] })), _jsx(Divider, { variant: "standard" }), _jsx(Flow, Object.assign({ ref: footerRef, className: cn.footer, orientation: "horizontal" }, { children: _jsxs(Flow, Object.assign({ orientation: "horizontal", spacing: "xxs", role: "group", className: cn.footerToolbar }, { children: [_jsx(Button, { className: cn.footerToolbarButton, variant: ButtonVariant.Light, ariaLabel: localize({
                                        key: 'PinPanel.addRoute.txt',
                                    }), text: localize({ key: 'PinPanel.addRoute.txt' }), icon: {
                                        icon: AddRoute,
                                    }, disabled: !validLocation, onPress: () => props.actions.notifyNewRoute(props.id, {
                                        sourceFeatureIndex: props.featureIndex,
                                        latLon: props.pin.latLon,
                                        location: props.pin.location,
                                    }), tooltip: localize({
                                        key: 'PinPanel.addRoute.tooltip.txt',
                                    }) }), props.canDoDistanceSelections ? (_jsx(Button, { className: cn.footerToolbarButton, variant: ButtonVariant.Light, ariaLabel: localize({
                                        key: 'PinPanel.addArea.txt',
                                    }), text: localize({ key: 'PinPanel.addArea.txt' }), icon: {
                                        icon: GeoUIAreaAddress,
                                        tooltip: localize({
                                            key: 'PinPanel.addArea.txt',
                                        }),
                                    }, disabled: !validLocation, onPress: () => {
                                        if (props.linkingEnabled) {
                                            setAddGeoAreaPopoverOpen(!addGeoAreaPopoverOpen);
                                        }
                                        else {
                                            props.actions.notifyNewArea(props.id, {
                                                sourceFeatureIndex: props.featureIndex,
                                                latLon: props.pin.latLon,
                                                location: props.pin.location,
                                                country: props.pin.country,
                                            });
                                        }
                                    }, ref: addGeoAreaButtonRef, tooltip: localize({
                                        key: 'PinPanel.addArea.tooltip.txt',
                                    }) })) : null, _jsxs(Popover, Object.assign({ referenceElementRef: addGeoAreaButtonRef, isOpen: addGeoAreaPopoverOpen, onClose: () => setAddGeoAreaPopoverOpen(false), role: "dialog" }, { children: [_jsx(Heading, Object.assign({ level: 5, className: cn.addAreaPopoverTitle, style: renderInlineCssVars({
                                                fg: getColorTextPrimary(dt),
                                                'spacing-m': dt.tokens['space-base-m'],
                                            }, 'addAreaPopoverTitle') }, { children: localize({ key: 'PinPanel.addArea.title.txt' }) })), _jsx(List, { rowCount: 2, expandToFillWidth: true, selectionMode: 'single', itemHeight: () => 'single', onSelect: (selectedRows) => {
                                                var _a;
                                                const actionMap = {
                                                    '0': () => {
                                                        props.actions.notifyNewArea(props.id, {
                                                            sourceFeatureIndex: props.featureIndex,
                                                            link: true,
                                                            linkedPinIndex: props.featureIndex,
                                                        });
                                                    },
                                                    '1': () => {
                                                        props.actions.notifyNewArea(props.id, {
                                                            sourceFeatureIndex: props.featureIndex,
                                                            latLon: props.pin.latLon,
                                                            location: props.pin.location,
                                                        });
                                                    },
                                                };
                                                setAddGeoAreaPopoverOpen(false);
                                                const selectedIndex = Array.from(selectedRows !== null && selectedRows !== void 0 ? selectedRows : [])[0];
                                                (_a = actionMap[selectedIndex]) === null || _a === void 0 ? void 0 : _a.call(actionMap);
                                            }, height: 30 * 2, width: 205, renderItem: ({ index }) => {
                                                let titleResourceKey;
                                                switch (index) {
                                                    case 0:
                                                        titleResourceKey = 'PinPanel.linkToPin.txt';
                                                        break;
                                                    case 1:
                                                        titleResourceKey = 'PinPanel.useCurrentLocation.txt';
                                                        break;
                                                }
                                                if (!titleResourceKey)
                                                    return null;
                                                return _jsx(Item, { title: localize({ key: titleResourceKey }) });
                                            } })] }))] })) }))] }))] })));
};
export const PinPanel = connect((state, props) => {
    var _a;
    const { features, autoLinkToPins, canDoDistanceSelections, pinModeEnabled, linkingEnabled, locationCandidate, } = (_a = selectObjectSearchState(state, props.id)) !== null && _a !== void 0 ? _a : {};
    const pin = features === null || features === void 0 ? void 0 : features[props.featureIndex];
    if (!pin || (pin.type !== 'pin' && pin.type !== 'myLocationPin')) {
        throw new Error('Pin not found');
    }
    return {
        autoLinkToPins,
        pin,
        features,
        canDoDistanceSelections,
        pinModeEnabled,
        linkingEnabled,
        locationCandidate,
    };
}, (dispatch) => ({
    actions: bindActionCreators(ObjectSearchState.actions, dispatch),
}))(PinPanelBase);
